import { formatDistanceToNow } from 'date-fns';
import { ExternalLink, Loader2 } from 'lucide-react';
import * as React from 'react';

import { useGetUpworkEscalationStatusQuery } from '@/services/api/generated/upworkEscalationsService';

interface UpworkEscalationStatusLineProps {
  taskId: string;
  matterId: string;
}

export const UpworkEscalationStatusLine: React.FC<
  UpworkEscalationStatusLineProps
> = ({ taskId, matterId }) => {
  const [, setClockTick] = React.useState(0);
  const { data: escalationStatus } = useGetUpworkEscalationStatusQuery(
    { matterId, taskId },
    {
      pollingInterval: 5000,
      skip: !matterId || !taskId,
    },
  );

  const hasActiveEscalation =
    escalationStatus?.has_active_upwork_escalation ?? false;

  React.useEffect(() => {
    if (!hasActiveEscalation) return undefined;

    const intervalId = window.setInterval(() => {
      setClockTick((current) => current + 1);
    }, 10000);

    return () => window.clearInterval(intervalId);
  }, [hasActiveEscalation]);

  let startedAtRelative: string | null = null;
  const startedAt = escalationStatus?.active_upwork_escalation_started_at;
  if (startedAt) {
    try {
      startedAtRelative = `Started ${formatDistanceToNow(new Date(startedAt), { addSuffix: true })}`;
    } catch {
      startedAtRelative = null;
    }
  }

  if (!hasActiveEscalation) {
    return null;
  }

  return (
    <div className="relative w-full overflow-hidden rounded-xl border border-orange-200/80 bg-orange-50/90 px-3 py-2 shadow-sm">
      <div className="upwork-escalation-shimmer pointer-events-none absolute inset-0 opacity-70" />
      <div className="relative flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5 shrink-0">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-orange-400/70" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-orange-500" />
            </span>
            <span className="truncate text-[12px] font-semibold leading-4 text-orange-800">
              Upwork escalation in progress
            </span>
          </div>
          <div className="mt-1 flex items-center gap-2 text-[11px] font-medium leading-4 text-orange-700/80">
            <span className="truncate">
              {startedAtRelative || 'Started recently'}
            </span>
            {escalationStatus?.workflow_id && (
              <>
                <span className="text-orange-300">&middot;</span>
                <span className="truncate">
                  {escalationStatus.workflow_id}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5 rounded-full bg-white/80 px-2.5 py-1 text-[11px] font-semibold text-orange-700 shadow-sm ring-1 ring-orange-200/80 backdrop-blur-sm">
          <ExternalLink className="h-3 w-3" />
          <Loader2 className="h-3 w-3 animate-spin" />
          Active
        </div>
      </div>
    </div>
  );
};
